import {
  Component, DestroyRef,
  EventEmitter, inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { BehaviorSubject } from "rxjs";

import { LorenModalData } from "../../../loren-dialog/interfaces/loren-modal-data.interface";
import { LorenEntityFormModel } from "../../../loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { LorenFieldTypes } from "../../../loren-entity-form-card/enums/loren-field-types.enum";
import { LorenFormField } from "../../../loren-entity-form-card/interfaces/loren-form-field.interface";
import { LorenDialogComponent } from "../../../loren-dialog/components/loren-dialog/loren-dialog.component";
import { LorenAsset } from "../../../../../features/loren-assets/interfaces/loren-asset.interface";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "loren-asset-selection-dialog",
    templateUrl: "./loren-asset-selection-dialog.component.html",
    styleUrls: ["./loren-asset-selection-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenAssetSelectionDialogComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);

  private readonly destroyRef = inject(DestroyRef);
  @Input() assets: LorenAsset[] = [];
  @Input() continueBtnLabel = "operations.continue";
  @Input() cancelBtnLabel = "operations.cancel";
  @Input() dialogTitle = "operations.choose_assets";
  @Input() dialogSubTitle = "";
  @Input() multiSelection = true;
  @Input() defaultValues: LorenAsset[] = [];
  @Output() finishSelection = new EventEmitter<LorenAsset[]>();
  @Output() cancelSelection = new EventEmitter();

  private dialogConfig: MatDialogConfig<LorenModalData>;
  private assetSelectionFormModel: LorenEntityFormModel;
  private allAssetsFormFields: LorenFormField[] = [];
  private reloadSubject = new BehaviorSubject<null>(null);
  private selectedAssets: LorenAsset[] = [];
  private dialogRef: MatDialogRef<LorenDialogComponent>;

  ngOnInit(): void {
    this.showAssetListDialog();
  }

  ngOnDestroy(): void {
    this.dialogRef.close();
  }

  showAssetListDialog() {
    this.assetSelectionFormModel = this.prepareAssetsSelectionFormModel();
    this.selectedAssets = [];

    const assetsListDialog: LorenModalData = {
      form: this.assetSelectionFormModel,
      modalAcceptBtnLabel: this.continueBtnLabel,
      modalCloseBtnLabel: this.cancelBtnLabel,
      title: this.dialogTitle,
      modalSubTitle: this.dialogSubTitle,
    };

    this.prepareDialog(assetsListDialog).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result) => {
      if (!result) {
        this.cancelSelection.emit();
        return;
      }

      if (this.multiSelection) {
        this.selectedAssets = this.assets.filter((x) => {
          return this.assetSelectionFormModel.formFields
            .filter((f) => Boolean(f.value) === true)
            .find((f) => Number(f.name) === x.id);
        });

        this.finishSelection.emit(this.selectedAssets);
      } else {
        const selectedAsset = this.assets.find(
          (x) => x.id === this.assetSelectionFormModel.formFields[0].value
        );
        this.finishSelection.emit([selectedAsset]);
      }
    });
  }

  private prepareAssetsSelectionFormModel() {
    const formModel: LorenEntityFormModel = {
      formFields: [],
      formName: "",
      reloadForm: this.reloadSubject,
    };

    if (this.multiSelection) {
      formModel.formFields.push({
        name: "searchAsset",
        label: "operations.search",
        placeholder: "operations.input_asset_name",
        formControlType: LorenFieldTypes.INPUT,
      });
      formModel.formFields.push({
        name: "selectAllAssets",
        label: "operations.select_all",
        look: "mb-3",
        formControlType: LorenFieldTypes.CHECKBOX,
      });
      this.prepareAllAssetsSelectionFields();
    } else {
      this.prepareSingleAssetSelectionField();
    }

    formModel.formFields.push(...this.allAssetsFormFields);

    return formModel;
  }

  private prepareSingleAssetSelectionField() {
    const assetField: LorenFormField = {
      name: `assetSelection`,
      label: `operations.select_asset`,
      value: this.assets[0].id,
      formControlType: LorenFieldTypes.SELECT,
      options: [],
    };

    this.assets.forEach((x) => {
      assetField.options.push({
        key: x.name,
        value: x.id,
      });
    });

    this.allAssetsFormFields.push(assetField);
  }

  private prepareAllAssetsSelectionFields() {
    this.allAssetsFormFields = [];
    this.assets.forEach((asset) => {
      const assetField: LorenFormField = {
        name: String(asset.id),
        label: `${asset.name}`,
        value: this.defaultValues.indexOf(asset) !== -1,
        formControlType: LorenFieldTypes.CHECKBOX,
      };

      if (asset.groups && asset.groups.length > 0) {
        assetField.label += ` (${asset.groups.join(",")})`;
      }

      this.allAssetsFormFields.push(assetField);
    });
  }

  private prepareDialog(modalData: LorenModalData) {
    this.dialogConfig = new MatDialogConfig();
    this.dialogConfig.disableClose = true;
    this.dialogConfig.autoFocus = true;
    this.dialogConfig.data = modalData;
    this.dialogConfig.hasBackdrop = false;

    this.dialogRef = this.dialog.open(LorenDialogComponent, this.dialogConfig);

    this.handleFormInteractions(modalData);

    return this.dialogRef.afterClosed();
  }

  private handleFormInteractions(modalData: LorenModalData) {
    modalData.form.formFieldInteraction.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((x) => {
      if (!x) {
        return;
      }

      const searchAssetField = this.assetSelectionFormModel.formFields.find(
        (f) => f.formControlType === LorenFieldTypes.INPUT
      );
      const selectAllAssets = this.assetSelectionFormModel.formFields.find(
        (f) => f.name === "selectAllAssets"
      );

      if (x.fieldName !== "selectAllAssets" && x.fieldCurrentValue === false) {
        this.assetSelectionFormModel.formFields.find(
          (f) => f.name === "selectAllAssets"
        ).value = false;
      }

      if (x.fieldName === "searchAsset") {
        let assetFields = this.allAssetsFormFields.filter(
          (f) =>
            f.formControlType === LorenFieldTypes.CHECKBOX &&
            f.label.toLowerCase().includes(x.fieldCurrentValue.toLowerCase())
        );

        if (assetFields.length === 0) {
          assetFields = [];
        }

        assetFields.unshift(selectAllAssets);
        assetFields.unshift(searchAssetField);

        this.assetSelectionFormModel.formFields = assetFields;
        this.reloadSubject.next(null);
      }

      if (x.fieldName === "selectAllAssets") {
        const assetFields = this.assetSelectionFormModel.formFields.filter(
          (f) => f.formControlType === LorenFieldTypes.CHECKBOX
        );

        assetFields.forEach((f) => (f.value = x.fieldCurrentValue));
        assetFields.unshift(searchAssetField);
        this.reloadSubject.next(null);
      }
    });
  }
}
